import { HttpClient } from '@angular/common/http';
import { inject, Injectable, makeStateKey, TransferState } from '@angular/core';
import { map, Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FilterForm } from '../models/filter-form.models';
import { QueryBuilder } from './query-builder';
import { ActiveJobsResponse } from '../../../shared/models/response';
import { Card } from '../models/job-card.model';
import { DefaultPageSize } from '../../../shared/consts/shared.const';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class JobSearchService {
  private http = inject(HttpClient);
  private transloco = inject(TranslocoService);
  private transferState = inject(TransferState);
  
  private activeLang = toSignal(this.transloco.langChanges$,{initialValue:'en'});
  private clientCache = new Map<string, {
    observable: Observable<ActiveJobsResponse<Card[]>>,
    timestamp: number
  }>();
  private CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  private STATE_KEY = makeStateKey<Record<string, ActiveJobsResponse<Card[]>>>('jobSearchCache');

  getJobs(filterform: FilterForm | null, pageNo: number = 1, pageSize = DefaultPageSize): Observable<ActiveJobsResponse<Card[]>> {
    const url = environment.searchEndpoint; // URL changed to dev again for keyword search improvement
    const toggleJobs = this.activeLang() === 'en' ? true : false;
    const payload = new QueryBuilder(filterform, pageNo, pageSize, toggleJobs);
    return this.getJobsWithCache(payload, url);
  }

  getJobSearchCount(filterform: FilterForm | null, pageNo: number, pageSize: number): Observable<number> {
    const toggleJobs = this.activeLang() === 'en';
    const payload = new QueryBuilder(filterform, pageNo, pageSize, toggleJobs);
    return this.http.get<{ TotalData: number }>(environment.searchCountEndpoint, {
      params: this.toQueryParams(payload),
    }).pipe(
      map((response) => response.TotalData)
    );
  }

  private toQueryParams(payload: QueryBuilder): Record<string, string | boolean> {
    return Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
    );
  }

  getJobsWithCache(payload: QueryBuilder, url: string): Observable<ActiveJobsResponse<Card[]>> {
    const key = JSON.stringify(payload);
    const isServer = typeof window === 'undefined';

    if (!isServer) {
      const transferredState = this.transferState.get<Record<string, ActiveJobsResponse<Card[]>>>(this.STATE_KEY, {});
      if (transferredState[key] !== undefined && transferredState[key] !== null) {
        const response = transferredState[key];
        delete transferredState[key];
        this.transferState.set(this.STATE_KEY, transferredState);
        
        const cachedResponse = of(response).pipe(
          shareReplay({ bufferSize: 1, refCount: true, windowTime: this.CACHE_DURATION })
        );
        this.clientCache.set(key, { observable: cachedResponse, timestamp: Date.now() });
        return cachedResponse;
      }

      if (this.clientCache.has(key)) {
        const cached = this.clientCache.get(key)!;
        const now = Date.now();
        
        if (now - cached.timestamp < this.CACHE_DURATION) {
          return cached.observable;
        } else {
          // Remove expired entry
          this.clientCache.delete(key);
        }
      }
    }

    const queryParams: Record<string, string | boolean> = Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined && value !== null)
    );
    const request = this.http.get<ActiveJobsResponse<Card[]>>(url, {
      params: queryParams,
    }).pipe(
      tap({
        next: (response) => {
          if (isServer) {
            const currentState = this.transferState.get<Record<string, ActiveJobsResponse<Card[]>>>(this.STATE_KEY, {});
            currentState[key] = response;
            this.transferState.set(this.STATE_KEY, currentState);
          }
        }
      }),
      shareReplay({ 
        bufferSize: 1, 
        refCount: true,
        windowTime: this.CACHE_DURATION 
      })
    );

    if (!isServer) {
      this.clientCache.set(key, { observable: request, timestamp: Date.now() });
    }
    return request;
  }
}